/**
 * "Process for withdrawal": the whole Available commission moves to Available for withdrawal, less a 5% service
 * charge. Exact integer-paisa math, rounded half up like the backend (`Money.splitFee`): fee + net == gross always.
 * 15000.75 → 750.04 / 14250.71; 501.50 → 25.08 / 476.42; 0.10 → 0.01 / 0.09.
 */
import { paisaToTaka, percentOfPaisa, toPaisa } from '../money';
import { SERVICE_CHARGE_PERCENT } from './withdrawalFee';

export interface ProcessingQuote {
  grossAmount: number;
  feeAmount: number;
  netAmount: number;
  feeRatePercent: number;
}

export function quoteProcessing(availableCommission: number | null | undefined): ProcessingQuote {
  const gross = Math.max(0, toPaisa(availableCommission ?? 0));
  const fee = percentOfPaisa(gross, SERVICE_CHARGE_PERCENT);
  return {
    grossAmount: paisaToTaka(gross),
    feeAmount: paisaToTaka(fee),
    netAmount: paisaToTaka(gross - fee),
    feeRatePercent: SERVICE_CHARGE_PERCENT,
  };
}

/**
 * Smallest gross Available commission (in taka, 2dp) whose net, after the 5% service charge, is >= `minNet`.
 * Exact integer-paisa search using the same rounding as quoteProcessing (round half up); no float division.
 * minNet = 300 -> 315.79 (315.79 -> fee 15.79 / net 300.00; 315.78 -> net 299.99). minNet <= 0 -> 0.
 */
export function minGrossToReach(minNet: number): number {
  const target = toPaisa(minNet);
  if (!(target > 0)) return 0;
  const net = (g: number) => g - percentOfPaisa(g, SERVICE_CHARGE_PERCENT);
  const keep = 100 - SERVICE_CHARGE_PERCENT;
  let g = Math.floor((target * 100 + keep - 1) / keep); // integer ceil estimate; then settle exactly
  while (g > 0 && net(g - 1) >= target) g -= 1;
  while (net(g) < target) g += 1;
  return paisaToTaka(g);
}

interface WalletSource {
  availableCommission?: number | null;
  availableForWithdrawal?: number | null;
  availableBalance?: number | null;
  availableCommissionBalance?: number | null;
  totalEarned?: number | null;
  totalCommissionEarned?: number | null;
}

export interface WalletBalances {
  totalEarned: number;
  /** Unprocessed commission. */
  availableCommission: number;
  /** What withdrawal requests debit. */
  availableForWithdrawal: number;
  /** False when the API predates the two-stage flow: the Process button must then stay hidden. */
  processingSupported: boolean;
}

/** Tolerant read of the wallet figures from dashboard stats / commission summary (older APIs lack the new fields). */
export function resolveWallet(...sources: Array<WalletSource | null | undefined>): WalletBalances {
  const pick = (k: keyof WalletSource): number | null => {
    for (const s of sources) {
      const v = s?.[k];
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    return null;
  };
  const commission = pick('availableCommission');
  return {
    totalEarned: pick('totalCommissionEarned') ?? pick('totalEarned') ?? 0,
    availableCommission: commission ?? 0,
    availableForWithdrawal:
      pick('availableForWithdrawal') ?? pick('availableBalance') ?? pick('availableCommissionBalance') ?? 0,
    processingSupported: commission !== null,
  };
}
